/**
 * SystemBus - Event Communication System
 *
 * PURPOSE: Pub/sub event bus for cross-system communication
 * WHY: Decouples components, eliminates prop drilling, enables reactive updates
 *
 * ARCHITECTURE:
 * - Publisher/Subscriber pattern
 * - Components subscribe to events they care about
 * - Publishers emit events when state changes
 * - Subscribers automatically receive updates
 *
 * BENEFITS:
 * - No tight coupling between components
 * - Easy to add new features without modifying existing code
 * - Clear event flow for debugging
 * - Automatic updates across the app
 *
 * USAGE EXAMPLE:
 * ```javascript
 * import { systemBus, EVENTS } from './services/systemBus';
 *
 * // Subscribe to events
 * systemBus.on(EVENTS.STATS_CHANGED, (data) => {
 *   console.log('Stats changed:', data);
 *   recalculateAC();
 * });
 *
 * // Emit events
 * systemBus.emit(EVENTS.STATS_CHANGED, {
 *   ability: 'strength',
 *   oldValue: 16,
 *   newValue: 18,
 *   source: 'asi'
 * });
 *
 * // Unsubscribe when component unmounts
 * const unsubscribe = systemBus.on(EVENTS.STATS_CHANGED, handler);
 * unsubscribe(); // Remove listener
 * ```
 *
 * Created: December 10, 2024
 * Part of: Master Implementation Plan - Week 1, Day 3-4
 */

/**
 * Event definitions — each event documents what data it carries.
 *
 * [S4 WIRING AUDIT — April 2026]
 * Events marked RESERVED have one side wired (emit or subscribe) but not both.
 * They are intentional infrastructure for planned features — do not remove.
 *
 * Emit-only (no subscriber yet — activity log / analytics candidates):
 *   DEATH_SAVE_SUCCESS, DEATH_SAVE_FAILURE, DEATH_SAVE_ROLLED,
 *   EXHAUSTION_CHANGED, TEMP_HP_CHANGED, PROFICIENCY_GAINED,
 *   FEAT_ABILITY_USED, ITEM_ABILITY_USED, EFFECT_STACKING_PREVENTED,
 *   COMBAT_CONDITION_APPLIED, COMBAT_CONDITION_REMOVED,
 *   CONCENTRATION_CHECK_REQUESTED, REST_LONG_STARTED, REST_SHORT_STARTED,
 *   CLASS_CREATED, CLASS_EDITED, SUBCLASS_CREATED, SUBCLASS_EDITED,
 *   SHARED_VAULT_ITEM_ADDED, SHARED_VAULT_ITEM_REMOVED, SHARED_VAULT_ITEM_TAKEN,
 *   DM_PUSHED_EVENT, DM_PUSHED_LOOT, DM_PUSHED_NOTE, DM_PUSHED_SHOP,
 *   SPELL_CAST_ON_ALLY, SPELL_REMOVED_FROM_ALLY
 *
 * Subscribe-only (no emitter yet — listeners ready in components):
 *   SETTINGS_CHANGED, CHARACTER_BASICS_CHANGED, CALENDAR_DAY_CHANGED,
 *   INVENTORY_CHANGED, ITEM_ADDED, ITEM_PURCHASED, ITEM_REMOVED, LOOT_RECEIVED,
 *   COMBAT_ATTACK, COMBAT_DAMAGE, COMBAT_HEALING,
 *   FEATURE_USED, DICE_ROLLED, CUSTOM_TABLE_CREATED,
 *   TABLE_ROLLED, TABLE_EDITED, TABLE_DELETED,
 *   JOURNAL_ENTRY_EDITED, JOURNAL_ENTRY_DELETED,
 *   PARTY_SYNC, PARTY_MESSAGE, PARTY_MEMBER_ADDED, PARTY_MEMBER_UPDATED,
 *   SOURCE_CREATED, SOURCE_CONTENT_ADDED, SOURCE_CONTENT_UPDATED
 */
export const EVENTS = {
  // ========================================
  // CHARACTER EVENTS
  // ========================================

  /** Character ability scores changed
   * Data: { ability, oldValue, newValue, source }
   * Sources: 'asi', 'feat', 'item', 'spell', 'potion', 'level_up'
   */
  STATS_CHANGED: 'stats.changed',

  /** Character gained a level
   * Data: { oldLevel, newLevel, className, isMulticlass }
   */
  LEVEL_UP: 'character.level_up',

  /** Character hit points changed
   * Data: { oldHP, newHP, maxHP, damage, healing, source }
   */
  HP_CHANGED: 'character.hp_changed',

  /** Temporary HP gained or lost
   * Data: { oldTempHP, newTempHP, source }
   */
  TEMP_HP_CHANGED: 'character.temp_hp_changed',

  /** Character died or was revived
   * Data: { isDead, deathSaves }
   */
  DEATH_STATE_CHANGED: 'character.death_state_changed',

  /** Exhaustion level changed
   * Data: { oldLevel, newLevel }
   */
  EXHAUSTION_CHANGED: 'character.exhaustion_changed',

  /** Inspiration gained or used
   * Data: { oldCount, newCount, source }
   */
  INSPIRATION_CHANGED: 'character.inspiration_changed',

  /** Specific ability score changed
   * Data: { ability, oldValue, newValue, source }
   * Sources: 'asi', 'feat', 'item', 'spell', 'potion', 'level_up', 'manual'
   */
  ABILITY_SCORE_CHANGED: 'character.ability_score_changed',

  /** Experience points changed
   * Data: { oldXP, newXP, change, source }
   * Sources: 'manual', 'quest', 'combat', 'milestone', 'import'
   */
  XP_CHANGED: 'character.xp_changed',

  /** Character class changed
   * Data: { oldClass, newClass, level, isMulticlass }
   * Sources: 'creation', 'level_up', 'import', 'manual'
   */
  CLASS_CHANGED: 'character.class_changed',

  /** Character subclass changed
   * Data: { className, oldSubclass, newSubclass, level }
   */
  SUBCLASS_CHANGED: 'character.subclass_changed',

  /** Character race changed
   * Data: { oldRace, newRace, source }
   * Sources: 'creation', 'magic', 'import', 'manual'
   */
  RACE_CHANGED: 'character.race_changed',

  /** Character background changed
   * Data: { oldBackground, newBackground, source }
   */
  BACKGROUND_CHANGED: 'character.background_changed',

  /** Character loaded from save/autosave
   * Data: { characterName, characterId, source: 'save'|'autosave'|'import' }
   * [FIX APR 2026]: Added for effectRegistry.clear() on character switch
   */
  CHARACTER_LOADED: 'character.loaded',

  /** Character state reset (new character or manual reset)
   * Data: { reason: 'new_character'|'manual_reset'|'import' }
   * [FIX APR 2026]: Added for effectRegistry.clear() on character reset
   */
  CHARACTER_RESET: 'character.reset',

  /** Character basic info changed (name, race, background, alignment, etc.)
   * Data: { field, oldValue, newValue }
   * [S4 RESERVED]: Listener in LanguagePicker — emitter not yet wired
   */
  CHARACTER_BASICS_CHANGED: 'character.basics_changed',

  // [ROSTER GALLERY 2026-05-10]: Roster-tab actions
  /** Character renamed from the Roster Gallery tab
   * Data: { characterId, oldName, newName }
   */
  CHARACTER_RENAMED: 'character.renamed',
  /** Character archived from the Roster Gallery tab
   * Data: { characterId, name }
   */
  CHARACTER_ARCHIVED: 'character.archived',
  /** Character unarchived from the Roster Gallery tab
   * Data: { characterId, name }
   */
  CHARACTER_UNARCHIVED: 'character.unarchived',

  /** Skill proficiency changed
   * Data: { skill, oldLevel, newLevel, source }
   * Levels: 'none', 'proficient', 'expert'
   * Sources: 'class', 'background', 'feat', 'item', 'manual'
   */
  SKILL_CHANGED: 'character.skill_changed',

  /** App settings changed
   * Data: { setting, oldValue, newValue }
   * [S4 RESERVED]: Listener in MobileNavigation — emitter not yet wired
   */
  SETTINGS_CHANGED: 'settings.changed',

  /** Proficiency gained (new skill, weapon, tool, etc.)
   * Data: { type, name, source }
   * [S4 RESERVED]: Emitter exists — no subscriber yet (activity log candidate)
   */
  PROFICIENCY_GAINED: 'character.proficiency_gained',

  /** Class or racial feature used
   * Data: { featureId, featureName, usesRemaining, maxUses, rechargeType }
   * Examples: Channel Divinity, Bardic Inspiration, Action Surge
   */
  FEATURE_USED: 'character.feature_used',

  /** Death saving throw succeeded
   * Data: { roll, successes, failures, isStabilized }
   */
  DEATH_SAVE_SUCCESS: 'character.death_save_success',

  /** Death saving throw failed
   * Data: { roll, successes, failures, isDead }
   */
  DEATH_SAVE_FAILURE: 'character.death_save_failure',

  /** Death saving throw rolled (any result)
   * Data: { roll, successes, failures, isStabilized, isDead }
   * [S4 RESERVED]: Emitter exists — no subscriber yet (activity log candidate)
   */
  DEATH_SAVE_ROLLED: 'character.death_save_rolled',

  // ========================================
  // FEAT EVENTS
  // ========================================

  /** Feat added to character
   * Data: { featId, featName, passiveEffects, source }
   * Sources: 'asi', 'level_up', 'bonus', 'import'
   */
  FEAT_ADDED: 'feat.added',

  /** Feat removed from character
   * Data: { featId, featName, passiveEffects }
   */
  FEAT_REMOVED: 'feat.removed',

  /** Feat toggle activated/deactivated
   * Data: { featId, toggleId, isActive, effects }
   * Example: Great Weapon Master power attack toggle
   */
  FEAT_TOGGLED: 'feat.toggled',

  /** Feat active ability used
   * Data: { featId, abilityId, target, result }
   * Example: Action Surge, Second Wind, etc.
   */
  FEAT_ABILITY_USED: 'feat.ability_used',

  // ========================================
  // EQUIPMENT EVENTS
  // ========================================

  /** Item equipped to a slot
   * Data: { item, slot, previousItem, statChanges }
   */
  ITEM_EQUIPPED: 'equipment.equipped',

  /** Item unequipped from a slot
   * Data: { item, slot, statChanges }
   */
  ITEM_UNEQUIPPED: 'equipment.unequipped',

  /** Item attuned
   * Data: { item, attunementSlot, requiresAttunement }
   */
  ITEM_ATTUNED: 'equipment.attuned',

  /** Item attunement broken
   * Data: { item, attunementSlot, reason }
   * Reasons: 'manual', 'death', 'item_lost', 'new_attunement'
   */
  ITEM_UNATTUNED: 'equipment.unattuned',

  /** Item active ability used
   * Data: { item, abilityId, chargesUsed, chargesRemaining, cooldown }
   * Example: Staff of Fire fireball cast
   */
  ITEM_ABILITY_USED: 'equipment.ability_used',

  /** Item identified — `isIdentified` flipped false → true
   * Data: { item }
   * [SUB-PLAN 1C ITEM 3 / 2026-05-23]: Subscribers (e.g., future party-sync
   *   broadcaster, activity-log analytics) react to the reveal. The flag
   *   itself lives on the inventory item; the event surfaces the transition.
   */
  ITEM_IDENTIFIED: 'equipment.identified',

  /** Item charges recharged
   * Data: { item, oldCharges, newCharges, rechargeType }
   * RechargeTypes: 'dawn', 'short_rest', 'long_rest', 'manual'
   */
  ITEM_RECHARGED: 'equipment.recharged',

  /** Equipment slots changed (general catch-all)
   * Data: { changedSlots, source }
   * Sources: 'equip', 'unequip', 'attune', 'unattune', 'swap'
   */
  EQUIPMENT_CHANGED: 'equipment.changed',

  // ========================================
  // QUICK BONUS EVENTS
  // ========================================

  /** Temporary quick bonus added
   * Data: { bonusId, bonusName, bonusType, value, duration, source }
   * Types: 'attack', 'damage', 'save', 'check', 'ac', 'initiative'
   * Sources: 'bless', 'guidance', 'bardic_inspiration', 'custom'
   */
  QUICK_BONUS_ADDED: 'quick_bonus.added',

  /** Temporary quick bonus removed
   * Data: { bonusId, bonusName, reason }
   * Reasons: 'expired', 'consumed', 'manual', 'rest'
   */
  QUICK_BONUS_REMOVED: 'quick_bonus.removed',

  // ========================================
  // SPELL EVENTS
  // ========================================

  /** Spell cast
   * Data: { spell, level, upcast, target, components, result }
   */
  SPELL_CAST: 'spell.cast',

  /** Spell learned
   * Data: { spell, source, spellbook }
   * Sources: 'level_up', 'scroll', 'trainer', 'item'
   */
  SPELL_LEARNED: 'spell.learned',

  /** Spell prepared
   * Data: { spell, isPrepared }
   */
  SPELL_PREPARED: 'spell.prepared',

  /** Spell unprepared
   * Data: { spellName, spellLevel, source }
   */
  SPELL_UNPREPARED: 'spell.unprepared',

  /** Concentration started on a spell
   * Data: { spell, duration, effects }
   */
  CONCENTRATION_STARTED: 'spell.concentration_started',

  /** Concentration broken
   * Data: { spell, reason }
   * Reasons: 'damage', 'new_concentration', 'incapacitated', 'death', 'manual'
   */
  CONCENTRATION_BROKEN: 'spell.concentration_broken',

  /** Spell slot used
   * Data: { level, slotsRemaining }
   */
  SPELL_SLOT_USED: 'spell.slot_used',

  /** Spell slots recovered
   * Data: { level, slotsRecovered, source }
   * Sources: 'long_rest', 'short_rest', 'arcane_recovery', 'item'
   */
  SPELL_SLOTS_RECOVERED: 'spell.slots_recovered',

  /** Custom spell created
   * Data: { spell, spellName, level, school, source }
   * Sources: 'wizard', 'custom_creator', 'import'
   */
  SPELL_CREATED: 'spell.created',

  /** Custom spell edited
   * Data: { spell, spellName, oldName, changes }
   */
  SPELL_EDITED: 'spell.edited',

  /** Custom catalog item created via Content Workshop Item Creator
   * Data: { item, sourceId }
   * [STONE 6 / #70 — 2026-05-18]: Catalog-authoring event. DISTINCT from
   *   INVENTORY_ITEM_ADDED / ITEM_EDITED (which are inventory-instance
   *   events). This fires when a new item enters the catalog via
   *   addItemToSource(sourceId, item).
   */
  ITEM_CATALOG_CREATED: 'item.catalog_created',

  /** Custom catalog item edited via Content Workshop Item Creator
   * Data: { item, sourceId, oldName? }
   */
  ITEM_CATALOG_EDITED: 'item.catalog_edited',

  /** Custom feat authored via Content Workshop (Stone 6 closure, 2026-05-18).
   * Data: { feat }
   * Subscriber: CharacterContext writes featData to playerStats.customFeats
   *   (see App.jsx CustomFeatCreator mount handler). Workshop listing /
   *   FeatsPanel refresh on this event for instant visibility.
   */
  CUSTOM_FEAT_CREATED: 'feat.custom_created',

  /** Custom feat edited via Content Workshop (Stone 6 closure).
   * Data: { feat }
   */
  CUSTOM_FEAT_EDITED: 'feat.custom_edited',

  /** Custom condition created via CustomConditionCreator (Sub-plan 3B, 2026-05-29).
   * Data: { condition } — the full condition object written to playerStats.customConditions.
   * Subscriber: ConditionsTracker refresh (getAllConditions memo invalidation via
   *   character context re-render when setPlayerStats is called).
   */
  CUSTOM_CONDITION_CREATED: 'condition.custom_created',

  /** Custom condition edited via CustomConditionCreator.
   * Data: { condition, oldId }
   */
  CUSTOM_CONDITION_EDITED: 'condition.custom_edited',

  /** Custom boon created via CustomBoonCreator (Sub-plan 3A, 2026-05-30).
   * Data: { boon } — the full boon object written to playerStats.customBoons.
   * Subscriber: useBoonEffects re-registers on playerStats.customBoons change
   *   (effectRegistry auto-refreshed via App.jsx functional setPlayerStats re-render).
   */
  CUSTOM_BOON_CREATED: 'boon.custom_created',

  /** Custom boon edited via CustomBoonCreator.
   * Data: { boon, oldId }
   */
  CUSTOM_BOON_EDITED: 'boon.custom_edited',

  /** Custom background created via CustomBackgroundCreator (Sub-plan 3C, 2026-05-30).
   * Data: { background } — the full background object written to playerStats.customBackgrounds.
   * Subscriber: CharacterBasicsPanel and CharacterCreationWizard pickers re-render via
   *   character context re-render when setPlayerStats is called (getCatalog('background')
   *   returns the new entry). No effectRegistry registration needed (Branch A narrative-only).
   * Daggerheart note: Branch A storage shape ({ id, name, featureName, featureDescription,
   *   personalityTraits?, bonds?, ideals?, flaws? }) generalises to "character-context document"
   *   for non-D&D systems (Daggerheart Experiences, CoC Occupations). See BREADCRUMB RSE-BG.
   */
  CUSTOM_BACKGROUND_CREATED: 'background.custom_created',

  /** Custom background edited via CustomBackgroundCreator.
   * Data: { background, oldId }
   */
  CUSTOM_BACKGROUND_EDITED: 'background.custom_edited',

  /** Spell cast on ally (party sync — supports buff/heal tracking across clients)
   * Data: { spell, caster, target, level, effects }
   * [S4 RESERVED]: Emitter exists — no subscriber yet (party sync candidate)
   */
  SPELL_CAST_ON_ALLY: 'spell.cast_on_ally',

  /** Spell effect removed from ally
   * Data: { spell, target, reason }
   * [S4 RESERVED]: Emitter exists — no subscriber yet (party sync candidate)
   */
  SPELL_REMOVED_FROM_ALLY: 'spell.removed_from_ally',

  /** Concentration check requested (damage taken while concentrating)
   * Data: { spell, damage, dc, rollRequired }
   * [S4 RESERVED]: Emitter exists — no subscriber yet (concentration UI candidate)
   */
  CONCENTRATION_CHECK_REQUESTED: 'spell.concentration_check_requested',

  /** Effect stacking prevented (D&D 5e rule enforcement)
   * Data: { spellName, reason, conflictingSource, conflictingMeta }
   * [PHASE 2.2 - NOV 15, 2025]: D&D 5e PHB p.205 - Same spell doesn't stack
   */
  EFFECT_STACKING_PREVENTED: 'effect.stacking_prevented',

  /** Effects changed (registered or removed from effectRegistry)
   * Data: { sourceId, action: 'register'|'remove', metadata }
   * [FIX NOV 26, 2025]: Added missing event used by effectRegistry.js
   */
  EFFECTS_CHANGED: 'effect.changed',

  // ========================================
  // REST EVENTS
  // ========================================

  /** Short rest started
   * Data: { duration, hitDiceAvailable }
   */
  REST_SHORT_STARTED: 'rest.short_started',

  /** Short rest completed
   * Data: { hitDiceUsed, hpRecovered, abilitiesRecovered }
   */
  REST_SHORT_COMPLETED: 'rest.short_completed',

  /** Long rest started
   * Data: { location, isInterrupted }
   */
  REST_LONG_STARTED: 'rest.long_started',

  /** Long rest completed
   * Data: { hpRecovered, hitDiceRecovered, spellSlotsRecovered, abilitiesRecovered }
   */
  REST_LONG_COMPLETED: 'rest.long_completed',

  // ========================================
  // CALENDAR/TIME EVENTS
  // ========================================

  /** New day started (dawn)
   * Data: { date, dayOfWeek, holiday }
   */
  CALENDAR_DAWN: 'calendar.dawn',

  /** Time progressed
   * Data: { hours, minutes, oldDate, newDate }
   */
  CALENDAR_TIME_ADVANCED: 'calendar.time_advanced',

  /** Calendar day boundary crossed (distinct from dawn or arbitrary time advance)
   * Data: { oldDay, newDay, date }
   * [S4 RESERVED]: Listener in DowntimesPanel — emitter not yet wired
   */
  CALENDAR_DAY_CHANGED: 'calendar.day_changed',

  /** Important date/event reached
   * Data: { eventName, eventType, description }
   */
  CALENDAR_EVENT: 'calendar.event',

  /** [R1 D2]: A gameplay day was advanced. Coordinates rest, calendar,
   * downtime, recharge logic, bastion turn — every subsystem that
   * needs to react to "a day passed."
   *
   * Distinct from CALENDAR_DAY_CHANGED, which fires on calendar-view
   * date boundary crossings. DAY_ADVANCED is the gameplay-time event.
   *
   * Data: { from: number, to: number, source: string, days: number }
   *   from   — gameplay day before advance
   *   to     — gameplay day after advance
   *   source — what triggered the advance (e.g. 'long-rest', 'downtime',
   *            'manual', 'bastion-turn')
   *   days   — number of days advanced (to - from); always >= 1
   */
  DAY_ADVANCED: 'day.advanced',

  // ========================================
  // PARTY EVENTS
  // ========================================

  /** Joined a party
   * Data: { partyId, partyName, members }
   */
  PARTY_JOINED: 'party.joined',

  /** Left a party
   * Data: { partyId, reason }
   */
  PARTY_LEFT: 'party.left',

  /** Party data synchronized
   * Data: { members, sharedData, timestamp }
   */
  PARTY_SYNC: 'party.sync',

  /** Party message received
   * Data: { from, message, timestamp, type }
   * Types: 'chat', 'roll', 'heal', 'item', 'gold', 'calendar'
   */
  PARTY_MESSAGE: 'party.message',

  /** DM pushed content to party
   * Data: { contentType, content, recipients }
   * ContentTypes: 'table', 'item', 'gold', 'event', 'calendar'
   */
  PARTY_DM_PUSH: 'party.dm_push',

  /** DM pushed a calendar/story event to party
   * Data: { event, description, recipients }
   * [S4 RESERVED]: Emitter exists — no subscriber yet
   */
  DM_PUSHED_EVENT: 'party.dm_pushed_event',

  /** DM pushed loot to party
   * Data: { loot, recipients }
   * [S4 RESERVED]: Emitter exists — no subscriber yet
   */
  DM_PUSHED_LOOT: 'party.dm_pushed_loot',

  /** DM pushed a note to party
   * Data: { note, recipients }
   * [S4 RESERVED]: Emitter exists — no subscriber yet
   */
  DM_PUSHED_NOTE: 'party.dm_pushed_note',

  /** DM pushed a shop to party
   * Data: { shop, recipients }
   * [S4 RESERVED]: Emitter exists — no subscriber yet
   */
  DM_PUSHED_SHOP: 'party.dm_pushed_shop',

  /** Party member added
   * Data: { memberId, memberName, characterData }
   */
  PARTY_MEMBER_ADDED: 'party.member_added',

  /** Party member updated
   * Data: { memberId, memberName, changes, characterData }
   */
  PARTY_MEMBER_UPDATED: 'party.member_updated',

  // ========================================
  // COMBAT EVENTS
  // ========================================

  /** Combat started
   * Data: { participants, initiativeOrder }
   */
  COMBAT_STARTED: 'combat.started',

  /** Turn started
   * Data: { character, turnNumber, round }
   */
  COMBAT_TURN_STARTED: 'combat.turn_started',

  /** Attack made
   * Data: { attacker, target, weapon, attackRoll, hit, damage }
   */
  COMBAT_ATTACK: 'combat.attack',

  /** Damage dealt
   * Data: { source, target, damageType, amount, critical }
   */
  COMBAT_DAMAGE: 'combat.damage',

  /** Healing received
   * Data: { source, target, amount, type }
   * Types: 'spell', 'potion', 'item', 'rest', 'feature'
   */
  COMBAT_HEALING: 'combat.healing',

  /** Condition applied
   * Data: { target, condition, duration, source }
   */
  COMBAT_CONDITION_APPLIED: 'combat.condition_applied',

  /** Condition removed
   * Data: { target, condition, reason }
   */
  COMBAT_CONDITION_REMOVED: 'combat.condition_removed',

  /** Combat ended
   * Data: { rounds, duration, outcome }
   */
  COMBAT_ENDED: 'combat.ended',

  // ========================================
  // INVENTORY EVENTS
  // ========================================

  /** Item added to inventory
   * Data: { item, quantity, source }
   * Sources: 'purchase', 'loot', 'craft', 'gift', 'import'
   */
  INVENTORY_ITEM_ADDED: 'inventory.item_added',

  /** Item removed from inventory
   * Data: { item, quantity, reason }
   * Reasons: 'sold', 'consumed', 'lost', 'gifted', 'destroyed'
   */
  INVENTORY_ITEM_REMOVED: 'inventory.item_removed',

  /** Item moved in grid
   * Data: { item, oldPosition, newPosition }
   */
  INVENTORY_ITEM_MOVED: 'inventory.item_moved',

  /** Item transferred to/from vault
   * Data: { item, quantity, vault, direction }
   * Directions: 'to_vault', 'from_vault'
   */
  INVENTORY_VAULT_TRANSFER: 'inventory.vault_transfer',

  /** Item properties edited
   * Data: { item, itemId, changes, oldProperties, newProperties }
   */
  ITEM_EDITED: 'inventory.item_edited',

  /** Generic catch-all for any inventory change (bulk ops, import, reset)
   * Data: { changeType, source }
   * [S4 RESERVED]: Listeners in ItemDialog, ShopTab — emitter not yet wired
   */
  INVENTORY_CHANGED: 'inventory.changed',

  /** Generic item-added alias (simpler payload than INVENTORY_ITEM_ADDED)
   * Data: { item, source }
   * [S4 RESERVED]: Listener in ActivityLogTab — emitter not yet wired
   */
  ITEM_ADDED: 'item.added',

  /** Generic item-removed alias
   * Data: { item, reason }
   * [S4 RESERVED]: Listener in ActivityLogTab — emitter not yet wired
   */
  ITEM_REMOVED: 'item.removed',

  /** Item purchased from shop
   * Data: { item, cost, shopkeeper }
   * [S4 RESERVED]: Listeners in CurrencyPanel, ShopTab — emitter not yet wired
   */
  ITEM_PURCHASED: 'item.purchased',

  /** Loot received (generic drop/reward, distinct from shop purchase)
   * Data: { items, currency, source }
   * [S4 RESERVED]: Listener in CurrencyPanel — emitter not yet wired
   */
  LOOT_RECEIVED: 'loot.received',

  /** Item added to a shared (party) vault
   * Data: { item, vaultId, depositor }
   * [S4 RESERVED]: Emitter exists — no subscriber yet (party sync candidate)
   */
  SHARED_VAULT_ITEM_ADDED: 'vault.shared_item_added',

  /** Item removed from a shared vault (by depositor)
   * Data: { item, vaultId, depositor }
   * [S4 RESERVED]: Emitter exists — no subscriber yet (party sync candidate)
   */
  SHARED_VAULT_ITEM_REMOVED: 'vault.shared_item_removed',

  /** Item taken from a shared vault (by another party member)
   * Data: { item, vaultId, takenBy }
   * [S4 RESERVED]: Emitter exists — no subscriber yet (party sync candidate)
   */
  SHARED_VAULT_ITEM_TAKEN: 'vault.shared_item_taken',

  // ========================================
  // CURRENCY EVENTS
  // ========================================

  /** Currency changed
   * Data: { type, oldAmount, newAmount, change, source }
   * Types: 'cp', 'sp', 'ep', 'gp', 'pp'
   * Sources: 'purchase', 'sale', 'loot', 'reward', 'manual'
   */
  CURRENCY_CHANGED: 'currency.changed',

  // ========================================
  // ACTIVITY LOG EVENTS
  // ========================================

  /** Activity logged
   * Data: { message, category, details, timestamp }
   * Categories: 'character', 'combat', 'spells', 'inventory', 'party', 'dice'
   */
  ACTIVITY_LOGGED: 'activity.logged',

  /** Activity log entry deleted
   * Data: { entryId }
   */
  ACTIVITY_LOG_ENTRY_DELETED: 'activity_log.entry_deleted',

  // ========================================
  // JOURNAL EVENTS
  // ========================================

  /** Journal entry added
   * Data: { entry, entryId, title, category, tags }
   */
  JOURNAL_ENTRY_ADDED: 'journal.entry_added',

  /** Journal entry edited
   * Data: { entry, entryId, oldTitle, newTitle, changes }
   */
  JOURNAL_ENTRY_EDITED: 'journal.entry_edited',

  /** Journal entry deleted
   * Data: { entryId, title, category }
   */
  JOURNAL_ENTRY_DELETED: 'journal.entry_deleted',

  // ========================================
  // ROLL TABLE EVENTS
  // ========================================

  /** Roll table rolled
   * Data: { tableName, category, roll, result, saveAs }
   * SaveAs: 'journal', 'parchment', 'activity', 'none'
   */
  TABLE_ROLLED: 'table.rolled',

  /** Custom roll table created
   * Data: { table, tableName, category, type }
   * Types: 'simple', 'weighted'
   */
  CUSTOM_TABLE_CREATED: 'table.created',

  /** Roll table edited
   * Data: { table, tableName, oldName, changes }
   */
  TABLE_EDITED: 'table.edited',

  /** Roll table deleted
   * Data: { tableName, category }
   */
  TABLE_DELETED: 'table.deleted',

  // ========================================
  // DICE EVENTS
  // ========================================

  /** Dice rolled
   * Data: { dice, result, rolls, modifier, total, purpose }
   * Purpose: 'attack', 'damage', 'check', 'save', 'custom', 'random'
   */
  DICE_ROLLED: 'dice.rolled',

  // ========================================
  // CONTENT SOURCE EVENTS
  // ========================================

  /** Content source enabled
   * Data: { sourceName, sourceType, content }
   * SourceTypes: 'tashas', 'xanathars', 'fizbans', 'custom'
   */
  SOURCE_ENABLED: 'source.enabled',

  /** Content source disabled
   * Data: { sourceName, sourceType, affectedContent }
   */
  SOURCE_DISABLED: 'source.disabled',

  /** Custom content source created (distinct from enable/disable of existing source)
   * Data: { source, sourceId, sourceName }
   * [S4 RESERVED]: Listener in SourceContentDialog — emitter not yet wired
   */
  SOURCE_CREATED: 'source.created',

  /** Content added to a source (e.g., a new spell appended to custom source)
   * Data: { sourceId, contentType, content }
   * [S4 RESERVED]: Listener in SourceContentDialog — emitter not yet wired
   */
  SOURCE_CONTENT_ADDED: 'source.content_added',

  /** Content updated within a source
   * Data: { sourceId, contentType, contentId, changes }
   * [S4 RESERVED]: Listener in SourceContentDialog — emitter not yet wired
   */
  SOURCE_CONTENT_UPDATED: 'source.content_updated',

  // ========================================
  // CONTENT CREATOR EVENTS (FEB 2026)
  // ========================================

  /** Custom race created via Race Creator
   * Data: { race, raceName, source, sourceId }
   */
  RACE_CREATED: 'content.race_created',

  /** Custom race edited
   * Data: { race, raceName, oldName, changes, sourceId }
   */
  RACE_EDITED: 'content.race_edited',

  /** Custom class created via Class Creator
   * Data: { classData, className, source, sourceId }
   */
  CLASS_CREATED: 'content.class_created',

  /** Custom class edited
   * Data: { classData, className, oldName, changes, sourceId }
   */
  CLASS_EDITED: 'content.class_edited',

  /** Custom subclass created via Subclass Creator
   * Data: { subclass, subclassName, parentClass, source, sourceId }
   */
  SUBCLASS_CREATED: 'content.subclass_created',

  /** Custom subclass edited
   * Data: { subclass, subclassName, oldName, parentClass, changes, sourceId }
   */
  SUBCLASS_EDITED: 'content.subclass_edited',

  /** Sourcebook content mutated (any category) — add/overwrite/remove.
   * Emitted by sourcesManager.addContentToSource / removeContentFromSource so
   * non-reactive consumers (Workshop trio drawers, the F-060.A subclass panel)
   * can re-list without polling the singleton.
   * Data: { sourceId, category, contentKey, action: 'added'|'overwritten'|'removed' }
   */
  SOURCE_CONTENT_CHANGED: 'content.source_content_changed',

  // ========================================
  // VAULT EVENTS (Phase 5 Session 4)
  // ========================================

  /** Vault created
   * Data: { vault, vaultId, vaultName, location, cost }
   */
  VAULT_CREATED: 'vault.created',

  /** Vault edited
   * Data: { vault, vaultId, oldProperties, newProperties, changes }
   */
  VAULT_EDITED: 'vault.edited',

  /** Vault removed
   * Data: { vaultId, vaultName, location }
   */
  VAULT_REMOVED: 'vault.removed',

  /** Vault shared with party
   * Data: { vault, vaultId, partyMembers, permissions }
   * Permissions: 'view', 'deposit', 'withdraw', 'full'
   */
  VAULT_SHARED: 'vault.shared',

  // ========================================
  // BASTION EVENTS (Phase 5 Session 4)
  // ========================================

  /** Bastion turn completed
   * Data: { turnNumber, facilities, orders, results, rewards }
   */
  BASTION_TURN_COMPLETED: 'bastion.turn_completed',

  /** Bastion facility built
   * Data: { facility, facilityId, facilityType, location, cost }
   */
  BASTION_FACILITY_BUILT: 'bastion.facility_built',

  /** Bastion facility upgraded
   * Data: { facility, facilityId, oldLevel, newLevel, cost }
   */
  BASTION_FACILITY_UPGRADED: 'bastion.facility_upgraded',

  /** Craft order placed
   * Data: { order, orderId, itemName, facility, duration, cost }
   */
  BASTION_ORDER_PLACED: 'bastion.order_placed',

  /** Craft order completed
   * Data: { order, orderId, itemName, result, quality }
   * Quality: 'poor', 'standard', 'fine', 'masterwork'
   */
  BASTION_ORDER_COMPLETED: 'bastion.order_completed',

  // ==========================================================================
  // TUTORIAL EVENTS — emitted by TutorialContext, subscribed by App.jsx
  // ==========================================================================

  /** Tutorial system requests a tab change so targets can be found.
   * [FIX APR 2026 Phase 3C.1]: Before this event existed, tutorials shown from
   * the wrong tab left the spotlight grey because targets weren't in the DOM.
   * Data: { tab }  — a valid activeTab id (e.g. 'downtimes', 'party')
   */
  TUTORIAL_REQUEST_TAB: 'tutorial.request_tab',

  // ==========================================================================
  // ACCOUNT EVENTS — emitted by AuthContext, subscribed by CharacterContext
  //   so character-scoped activity logs capture meaningful account actions
  //   (Rule 13). AuthContext sits above CharacterContext in the provider
  //   tree so it can't import logActivity directly; the systemBus is the
  //   decoupled channel.
  // ==========================================================================

  /** User signed in (any provider). Data: { uid, email, provider } */
  SIGNED_IN: 'account.signed_in',
  /** User signed out. Data: { uid? } — uid may be null if already cleared. */
  SIGNED_OUT: 'account.signed_out',
  /** Anonymous → authenticated link OR Google linked to existing account.
   *  Data: { uid, provider } */
  ACCOUNT_LINKED: 'account.linked',
  /** Linked provider unlinked. Data: { uid, provider } */
  ACCOUNT_UNLINKED: 'account.unlinked',
  /** User-initiated account deletion. Data: { uid } */
  ACCOUNT_DELETED: 'account.deleted',
  /** Premium license purchased (Stripe webhook → Firebase → client refresh).
   *  Data: { uid, purchaseDate } */
  PREMIUM_PURCHASED: 'account.premium_purchased',
  /** AI tokens purchased. Data: { uid, tokensAdded, totalTokens } */
  TOKENS_PURCHASED: 'account.tokens_purchased',

  // ==========================================================================
  // IMAGE LIBRARY EVENTS (1F — 2026-05-30)
  // ==========================================================================

  /** Image added to playerStats.imageLibrary.images.
   *  Data: { kind, name, entityId?, entityKind?, source }
   *  Sources: 'paste-url', 'upload', 'ai-generated'
   *  Note: imageId is NOT included because addImage() generates the id internally
   *  and does not return it to the caller. IMAGE_REMOVED correctly includes imageId.
   *  [2026-05-30]: Removed imageId from JSDoc — it was never emitted by paste-url
   *  or upload paths in CharacterGalleryPanel, causing a spec mismatch.
   */
  IMAGE_ADDED: 'image.added',

  /** Image removed from playerStats.imageLibrary.images.
   *  Data: { imageId, kind, name, entityId?, entityKind? }
   */
  IMAGE_REMOVED: 'image.removed',
};

/**
 * SystemBus class
 * Implements publisher/subscriber pattern for event-driven architecture
 */
export class SystemBus {
  constructor() {
    // Map of event names to arrays of callback functions
    this.listeners = {};

    // Debug mode flag
    this.debug = false;

    // Event history for debugging (last 100 events)
    this.history = [];
    this.maxHistory = 100;
  }

  /**
   * Enable/disable debug logging
   */
  setDebug(enabled) {
    this.debug = enabled;
    if (enabled) {
      console.log('[SystemBus] Debug mode enabled');
    }
  }

  /**
   * Subscribe to an event
   *
   * @param {string} event - Event name (use EVENTS constants)
   * @param {Function} callback - Function to call when event is emitted
   * @returns {Function} Unsubscribe function
   */
  on(event, callback) {
    if (typeof callback !== 'function') {
      console.error('[SystemBus] Callback must be a function');
      return () => {};
    }

    if (!this.listeners[event]) {
      this.listeners[event] = [];
    }

    this.listeners[event].push(callback);

    if (this.debug) {
      console.log(`[SystemBus] Subscribed to ${event}`, callback.name || 'anonymous');
    }

    // Return unsubscribe function
    return () => this.off(event, callback);
  }

  /**
   * Unsubscribe from an event
   *
   * @param {string} event - Event name
   * @param {Function} callback - Callback to remove
   */
  off(event, callback) {
    if (!this.listeners[event]) return;

    this.listeners[event] = this.listeners[event].filter(cb => cb !== callback);

    if (this.debug) {
      console.log(`[SystemBus] Unsubscribed from ${event}`);
    }

    // Clean up empty listener arrays
    if (this.listeners[event].length === 0) {
      delete this.listeners[event];
    }
  }

  /**
   * Emit an event
   *
   * @param {string} event - Event name (use EVENTS constants)
   * @param {*} data - Data to pass to listeners
   */
  emit(event, data = {}) {
    // [RECURSION PROTECTION - NOV 17, 2025]: Prevent undefined event names
    // [P0 FIX APR 2026]: Throw in test env so any future emit-undefined regression
    //   fails CI immediately rather than slipping to production. Origin bug:
    //   user-session console showed CRITICAL emit-undefined from a Shop Tab onChange
    //   path. Preserves production log+return semantics so live users still degrade
    //   gracefully — bug fixed before publish, but the hard guard prevents
    //   recurrence.
    if (!event || event === 'undefined') {
      const message = '[SystemBus] CRITICAL: Attempted to emit undefined/null/falsy event name';
      const detail = { event, data, stack: new Error().stack };
      if (process.env.NODE_ENV === 'test') {
        // Hard fail in tests — surface bad emit sites in CI before they ship.
        throw new Error(`${message} (got: ${JSON.stringify(event)}). Use a valid EVENTS.* constant.`);
      }
      console.error(message, detail);
      return; // Early return — production keeps degrading gracefully.
    }

    const timestamp = Date.now();

    // [RECURSION PROTECTION - NOV 17, 2025]: Detect infinite loops
    if (!this._emitStack) this._emitStack = [];

    // Check if we're already emitting this exact event (potential infinite loop)
    if (this._emitStack.includes(event)) {
      console.error('[SystemBus] RECURSION DETECTED:', {
        event,
        stack: this._emitStack,
        message: 'Event is already being processed - infinite loop prevented'
      });
      return; // Prevent recursion
    }

    // Add to history
    this.history.push({ event, data, timestamp });
    if (this.history.length > this.maxHistory) {
      this.history.shift();
    }

    if (this.debug) {
      console.group(`[SystemBus] Event: ${event}`);
      console.log('Data:', data);
      console.log('Listeners:', this.listeners[event]?.length || 0);
      console.groupEnd();
    }

    // Push event onto stack before calling listeners
    this._emitStack.push(event);

    try {
      // Call all registered listeners
      const listeners = this.listeners[event] || [];
      listeners.forEach(callback => {
        try {
          callback(data);
        } catch (error) {
          console.error(`[SystemBus] Error in listener for ${event}:`, error);
        }
      });
    } finally {
      // Always pop from stack, even if error occurred
      this._emitStack.pop();
    }
  }

  /**
   * Emit an event after a delay
   *
   * @param {string} event - Event name
   * @param {*} data - Data to pass to listeners
   * @param {number} delay - Delay in milliseconds
   * @returns {number} Timeout ID (can be used with clearTimeout)
   */
  emitDelayed(event, data, delay) {
    return setTimeout(() => this.emit(event, data), delay);
  }

  /**
   * Subscribe to an event only once
   *
   * @param {string} event - Event name
   * @param {Function} callback - Function to call when event is emitted
   * @returns {Function} Unsubscribe function
   */
  once(event, callback) {
    const wrappedCallback = (data) => {
      callback(data);
      this.off(event, wrappedCallback);
    };

    return this.on(event, wrappedCallback);
  }

  /**
   * Check if an event has listeners
   *
   * @param {string} event - Event name
   * @returns {boolean} True if event has listeners
   */
  hasListeners(event) {
    return (this.listeners[event]?.length || 0) > 0;
  }

  /**
   * Get number of listeners for an event
   *
   * @param {string} event - Event name
   * @returns {number} Number of listeners
   */
  getListenerCount(event) {
    return this.listeners[event]?.length || 0;
  }

  /**
   * Remove all listeners for an event or all events
   *
   * @param {string} [event] - Event name (optional, removes all if not specified)
   */
  removeAllListeners(event) {
    if (event) {
      delete this.listeners[event];
      if (this.debug) {
        console.log(`[SystemBus] Removed all listeners for ${event}`);
      }
    } else {
      this.listeners = {};
      if (this.debug) {
        console.log('[SystemBus] Removed ALL listeners');
      }
    }
  }

  /**
   * Get event history
   *
   * @param {string} [event] - Filter by event name (optional)
   * @param {number} [limit] - Max number of events to return
   * @returns {Array} Array of event objects
   */
  getHistory(event, limit) {
    let history = [...this.history];

    if (event) {
      history = history.filter(h => h.event === event);
    }

    if (limit) {
      history = history.slice(-limit);
    }

    return history;
  }

  /**
   * Clear event history
   */
  clearHistory() {
    this.history = [];
  }

  /**
   * Get statistics about the event bus
   *
   * @returns {Object} Statistics object
   */
  getStats() {
    const totalListeners = Object.values(this.listeners).reduce((sum, arr) => sum + arr.length, 0);
    const eventTypes = Object.keys(this.listeners).length;
    const recentEvents = this.history.slice(-10).map(h => h.event);

    return {
      totalListeners,
      eventTypes,
      events: Object.keys(this.listeners),
      recentEvents,
      historySize: this.history.length
    };
  }
}

// [TDZ BUG FIX - NOV 17, 2025]: Changed from `const` to `var` to fix production minifier TDZ error
// [WHY]: Module-level `const` with instantiation can cause TDZ in minified bundles
// [CAUSE]: Webpack/minifier may reorder code, `var` is hoisted and has no TDZ
// Export singleton instance
export var systemBus = new SystemBus();

// Export for testing
export default SystemBus;
